import React, { useMemo } from 'react';
import { formatSectionRepeat } from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';

const createStyles = (theme: Theme) => ({
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    height: theme.sizes.miniButton,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    border: `1px solid ${theme.colors.borderSecondary}`,
    borderRadius: theme.radii.full,
    backgroundColor: 'transparent',
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.textSecondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
  },
  pillButton: {
    cursor: 'pointer',
  },
});

export interface SectionRepeatPillProps {
  /** Times the section is played (2..8). */
  repeat: number;
  /** Makes the pill a button (the editor opens the repeat picker). */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** The outlined "×2" pill shown after a section title. */
const SectionRepeatPill: React.FC<SectionRepeatPillProps> = ({ repeat, onClick }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const label = `Played ${repeat} times`;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...styles.pill, ...styles.pillButton }}
        aria-label={label}
        title={label}
        data-testid="section-repeat-pill"
      >
        {formatSectionRepeat(repeat)}
      </button>
    );
  }

  return (
    <span style={styles.pill} aria-label={label} data-testid="section-repeat-pill">
      {formatSectionRepeat(repeat)}
    </span>
  );
};

export default SectionRepeatPill;
